import { Alert, Table, Tag, Collapse, Typography } from "antd";
import React from "react";

const { Panel } = Collapse;
const { Text, Paragraph } = Typography;

const ImportLogsTable = ({ previewData, logData }) => {
  // Add safety check for invalid data
  let logs = [];
  try {
    logs = logData || previewData?.message?.import_log || [];
    if (!Array.isArray(logs)) {
      console.error("ImportLogsTable: logs is not an array:", logs);
      logs = [];
    }
  } catch (e) {
    console.error("ImportLogsTable: Error processing logs:", e);
    logs = [];
  }

  const formatLogData = (logs) => {
    if (!logs || !Array.isArray(logs)) return { columns: [], dataSource: [] };

    const columns = [
      {
        title: "Row Number",
        dataIndex: "rowNumbers",
        key: "rowNumbers",
        width: 120,
        render: (rowNumbers) => (
          <Tag color="blue">
            {Array.isArray(rowNumbers) ? rowNumbers.join(", ") : rowNumbers}
          </Tag>
        ),
      },
      {
        title: "Status",
        dataIndex: "success",
        key: "success",
        width: 100,
        render: (success) => (
          <Tag color={success ? "green" : "red"}>
            {success ? "Success" : "Failed"}
          </Tag>
        ),
      },
      {
        title: "Document Name",
        dataIndex: "docname",
        key: "docname",
        width: 150,
        render: (docname) => docname || "N/A",
      },
      {
        title: "Messages",
        dataIndex: "messages",
        key: "messages",
        render: (messages) => {
          if (!messages) return "No messages";

          // Handle array of objects (new format)
          if (Array.isArray(messages)) {
            if (messages.length === 0) return "No messages";
            return (
              <div>
                {messages.map((msg, index) => (
                  <div key={index} className="mb-1">
                    <Text type="danger" className="text-sm">
                      {typeof msg === "object" ? msg.message : msg}
                    </Text>
                  </div>
                ))}
              </div>
            );
          }

          // Handle JSON string (old format)
          try {
            const parsedMessages = JSON.parse(messages);
            if (Array.isArray(parsedMessages) && parsedMessages.length === 0) {
              return "No messages";
            }
            return (
              <div>
                {parsedMessages.map((msg, index) => (
                  <div key={index} className="mb-1">
                    <Text type="danger" className="text-sm">
                      {typeof msg === "object" ? msg.message : msg}
                    </Text>
                  </div>
                ))}
              </div>
            );
          } catch {
            return <Text type="danger">{messages}</Text>;
          }
        },
      },
    ];

    const formattedData = logs.map((log, index) => {
      let rowNumbers;
      try {
        rowNumbers = Array.isArray(log.row_indexes)
          ? log.row_indexes
          : JSON.parse(log.row_indexes);
      } catch {
        rowNumbers = log.row_indexes || "Unknown";
      }

      // Handle multiple formats:
      // - Old format: log.success is boolean
      // - New format: log.status is "success" or "error"
      // - Direct from API: item is in import_successes array (no success/status field)
      let isSuccess = false;
      if (typeof log.success === "boolean") {
        isSuccess = log.success;
      } else if (log.status === "success") {
        isSuccess = true;
      } else if (log.status === "error") {
        isSuccess = false;
      }

      return {
        key: index,
        rowNumbers,
        success: isSuccess,
        docname: log.docname || log.client_name || "",
        messages: log.messages,
        exception: log.exception,
      };
    });

    return {
      columns,
      dataSource: formattedData,
    };
  };

  const { columns, dataSource } = formatLogData(logs);

  if (!dataSource.length) {
    return (
      <div className="mt-6">
        <Alert
          message="No Logs Available"
          description="No import logs found to display."
          type="info"
          showIcon
        />
      </div>
    );
  }

  const successCount = dataSource.filter((item) => item.success).length;
  const failedCount = dataSource.length - successCount;
  const totalCount = dataSource.length;

  return (
    <div className="mt-6">
      <div className="mb-4">
        <Alert
          message="Import Logs"
          description={
            <div>
              <div>Total Records: {totalCount}</div>
              <div>
                <Text type="success">Successful: {successCount}</Text>
                {" | "}
                <Text type="danger">Failed: {failedCount}</Text>
              </div>
            </div>
          }
          type={failedCount > 0 ? "warning" : "success"}
          showIcon
        />
      </div>

      {/* <Table
        columns={columns}
        dataSource={dataSource}
        size="small"
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
          showQuickJumper: false,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} log entries`,
        }}
        scroll={{
          x: 1200,
          y: 400,
        }}
        bordered
        rowClassName={(record) =>
          record.success ? "success-row" : "error-row"
        }
      /> */}

      <style dangerouslySetInnerHTML={{ __html: `
        .success-row {
          background-color: #f6ffed;
        }
        .error-row {
          background-color: #fff2f0;
        }
      ` }} />
    </div>
  );
};

// Wrapper component with error boundary
const ImportLogsTableWrapper = (props) => {
  try {
    return <ImportLogsTable {...props} />;
  } catch (error) {
    console.error("ImportLogsTable crashed:", error);
    return (
      <Alert
        message="Error displaying import logs"
        description="An error occurred while rendering the import logs. Please check the console for details."
        type="error"
        showIcon
      />
    );
  }
};

export default ImportLogsTableWrapper;
